<template>
  <!-- 商店頁 -->
  <v-container v-resize="onResize" class="branches__container pa-0" fluid>
    <content-menu
      v-if="showContentMenu"
      class="branch-content-menu"
      :class="{ hidden: !showContentMenu }"
      :activated-menu="activatedMenu"
      :items="contentMenu"
    />

    <BranchBanner :branch="branch" hide-text />

    <div class="branch-content-container d-flex justify-space-between">
      <div class="branch-content">
        <div id="main-branch-address" class="branch-content__block">
          <!-- 商店資訊 -->
          <v-sheet class="products-info-card wd-py-4 sm:(wd-px-3) md:(wd-px-5)" elevation="0" rounded>
            <!-- branch card -->
            <FunBookV2BranchDetailCard :branch="branch" />
          </v-sheet>
        </div>

        <!-- 快速預訂 -->
        <MobileBookingCardSection
          v-if="isMobileScreen"
          ref="mobileBookingSection"
          :branch="branch"
          wrapper-class="wd-mb-2"
          skeleton-class="wd-mb-2 wd-h-53"
          @resolve="bookingBannerScrollEvent"
        />

        <!-- 分店 商品列表 -->
        <div v-if="productsUiList.length" id="main-branch-products" class="branch-content__block">
          <BranchProductList :products="productsUiList" :source="GtmSource.BRANCH_PAGE" />
        </div>
      </div>
      <client-only>
        <div v-if="!isMobile" ref="bookingCard" class="booking-card__web">
          <BookingBranchCard :branch="branch" :products="products" />
        </div>
      </client-only>
    </div>

    <!-- 該商家其他分店 -->
    <BranchSwiper v-if="otherBranches.length" id="main-other-branches" :title="otherBranchTitle" :items="otherBranches" hide-rating is-fun-book-v2 />

    <BranchBookingFooter v-if="isMobile && showStatusOfBookingBanner" v-bind="footerData" />

    <JsonLdItemList :branch="branch" :products="sortedBranchProducts" />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import ContentMenu from '@/components/navigation/content-menu.vue'
import BranchSwiper from '@/components/swiper/branch-swiper.vue'
import JsonLdItemList from '@/components/jsonld/itemList.vue'
import BranchBanner from '@/components/BranchBanner.vue'
import FunBookV2BranchDetailCard from '@/components/FunBookV2BranchDetailCard.vue'
import BranchBookingFooter from '@/components/BranchBookingFooter.vue'
import BranchProductList from '@/components/BranchProductList.vue'
import MobileBookingCardSection from '@/components/page/branches/mobile-booking-card-section.vue'

import utils from '@/utils'
import { sendViewBranchTrackingCode } from '@/utils/gtm'
import { useFunbookBranchFromStore } from '@/composition/useFunbookV2'
import { useBranchProducts } from '@/composition/useBranchProducts'
import { useBreakpoints } from '@/composition/useBreakpoints'
import { GtmSource } from '@/view-models/gtm'

const BookingBranchCard = defineAsyncComponent(() => import('@/components/card/booking-branch-card.vue'))

export default defineComponent({
  name: 'FunBookV2BranchDetail',
  components: {
    ContentMenu,
    BranchSwiper,
    BookingBranchCard,
    JsonLdItemList,
    BranchBanner,
    FunBookV2BranchDetailCard,
    BranchBookingFooter,
    BranchProductList,
    MobileBookingCardSection,
  },
  props: {
    branch: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const nuxApp = useNuxtApp()
    const i18n = useI18n()
    const { otherBranchTitle, otherBranches } = useFunbookBranchFromStore(computed(() => props.branch.bid))
    const { isMobile: isMobileScreen } = useBreakpoints()

    const products = computed(() => nuxApp.$store.getters['branch/productsForFunBook'])
    const { sortedBranchProducts } = useBranchProducts(products)

    useHead(() => {
      const pageTitle = `${props.branch.name} | ${i18n.t('funnow_title')}`
      const keywords = (props.branch.tags || []).map(tag => tag.name).join(',')
      const ogImage = utils.ogImageUrl(props.branch.cover_image)

      return {
        title: pageTitle,
        meta: [
          { hid: 'title', name: 'title', content: pageTitle },
          { hid: 'description', name: 'description', content: props.branch.fb_intro },
          { hid: 'keywords', name: 'keywords', content: keywords },
          { hid: 'og:title', property: 'og:title', content: pageTitle },
          { hid: 'og:description', property: 'og:description', content: props.branch.fb_intro },
          { hid: 'og:image', property: 'og:image', content: ogImage },
        ],
      }
    })

    onMounted(() => {
      sendViewBranchTrackingCode(nuxApp, props.branch, 'FunBook')
    })

    return {
      otherBranchTitle,
      otherBranches,
      sortedBranchProducts,
      products,
      GtmSource,
      isMobileScreen,
    }
  },
  data() {
    return {
      showContentMenu: false,
      /**
       * @deprecated use `isMobileScreen`.
       *
       * Not migrated in-place: this `isMobile` is `false` until `onMounted`
       * computes window width, while `isMobileScreen` is already truthy on
       * SSR for mobile UA. Swapping every reference may flip SSR output for
       * `v-if` / `:class` bindings, so callers should migrate case-by-case
       * after reviewing the SSR impact.
       */
      isMobile: false,
      showStatusOfBookingBanner: false,
      activatedMenu: 'branchInfo',
    }
  },
  computed: {
    loggedInUser() {
      return this.$auth.user
    },
    contentMenu() {
      return [
        {
          text: this.$t('all_branch_info'),
          value: 'branchInfo',
          targetID: 'main-branch-address',
          poissonID: 'main-branch-address',
        },
        {
          text: this.$t('all_package_list'),
          value: 'packageList',
          targetID: 'main-branch-products',
          poissonID: 'main-branch-products',
        },
        {
          text: this.$t('other_branch', { branchname: '' }),
          value: 'otherBranches',
          targetID: 'main-other-branches',
          poissonID: 'main-other-branches',
        },
      ]
    },
    productsUiList() {
      return this.sortedBranchProducts?.filter(p => !('has_detail' in p) || p.has_detail) || []
    },
    footerData() {
      return {
        branch: this.branch,
        ...this.selectedBookingPayload,
        noRating: true,
      }
    },
    ...mapGetters({
      groupOrderStatus: 'groupOrderStatus',
      groupOrder: 'groupOrder',
      selectedBookingPayload: 'branch/selectedBookingPayload',
    }),
  },
  mounted() {
    this.initContentMenu()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeUnmount() {
    this.$store.dispatch('notification/resetNotification')
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    initContentMenu() {
      this.setShowContentMenu()
      if (this.showContentMenu) {
        this.contentMenuScrollEvent()
      }
    },
    setShowContentMenu() {
      this.showContentMenu = this.$el?.parentElement?.getBoundingClientRect().top + 237 < 0
    },
    onResize() {
      // 1280: $lg-breakpoint: 1280px; 600: $xs-breakpoint: 600px
      this.isMobile = window.innerWidth < 600
    },
    nl2br(text) {
      return utils.nl2br(text)
    },
    contentMenuScrollEvent() {
      const pageBounding = this.$el?.parentElement?.getBoundingClientRect()
      const isPageBottom = pageBounding.bottom < document.documentElement.clientHeight
      if (isPageBottom) {
        this.activatedMenu = this.contentMenu[this.contentMenu.length - 1].value
      } else {
        let minRangeTarget = {}
        this.setShowContentMenu()
        this.contentMenu.forEach(menu => {
          if (document.getElementById(menu.poissonID)) {
            const elBounding = document.getElementById(menu.poissonID).getBoundingClientRect()
            const isDisappear = elBounding.height + elBounding.top - 128 < 0
            if (!isDisappear && elBounding.top < 150) {
              minRangeTarget = Object.assign({}, menu)
            }
          }
        })
        this.activatedMenu = minRangeTarget.value
      }
    },
    bookingBannerScrollEvent() {
      const bookingCardEl = this.$refs.mobileBookingSection?.bookingCardEl
      const parentEl = this.$el?.parentElement

      if (!bookingCardEl || !parentEl) {
        return
      }

      const $branchPage = parentEl.getBoundingClientRect()
      // 要 bookingCard 的位置是要讓 Banner 不要出現
      const $bookingCard = bookingCardEl.getBoundingClientRect()
      // 可視高度 = 視窗高度 - 預訂 banner高度
      const contentHeight = window.innerHeight - 64
      // 128: header高度 + 導覽列高度
      this.showStatusOfBookingBanner = !(contentHeight > $bookingCard.top && $bookingCard.bottom > 128) && $branchPage.bottom > window.innerHeight
    },
    handleScroll() {
      this.contentMenuScrollEvent()
      if (this.isMobile) {
        this.bookingBannerScrollEvent()
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@use '@/assets/sass/pages/branch-info.scss';
</style>
